import { useEffect, useMemo, useState } from 'react'
import { ref, update, remove } from 'firebase/database'
import { db } from '../lib/firebase.js'
import CLUB from '../config/club.js'

// Player Message — sends a big/bold overlay to PLAYERS' PHONES (the native app),
// NOT the TVs. Works in TWO modes:
//   • tournament mode  → writes tournaments/{tid}/playerMessage, targets by
//                        Everyone / Flight / Wave / Team.
//   • qualifying mode  → writes qualifyingEvents/{eid}/playerMessage, targets by
//                        Everyone / Team (no flights/waves exist pre-build).
// The app reads whichever path is live, so it works BEFORE a tournament is built.
const PRIMARY = CLUB.primaryColor || '#123D26'
const GOLD = CLUB.accentColor || '#C2A24E'

export default function PlayerMessageTab({ tournamentId, tournament, flights = {}, teeTimes = {}, eventId = null, qEvent = null }) {
  const qualifyingMode = !!eventId
  const [text, setText] = useState('')
  const [audType, setAudType] = useState('everyone') // everyone | flight | wave | team
  const [flightId, setFlightId] = useState('')
  const [wave, setWave] = useState('wave1')
  const [teamSel, setTeamSel] = useState('') // teamId
  const [teamQuery, setTeamQuery] = useState('') // search filter for the By-Team picker
  const [timing, setTiming] = useState('instant') // instant | scheduled
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const writePath = qualifyingMode ? `qualifyingEvents/${eventId}/playerMessage` : `tournaments/${tournamentId}/playerMessage`
  const current = qualifyingMode ? (qEvent?.playerMessage || null) : (tournament?.playerMessage || null)

  const flightList = useMemo(() => {
    if (qualifyingMode) return []
    return Object.entries(flights).filter(([fid]) => !String(fid).startsWith('_')).map(([fid, f]) => ({ fid, name: f?.info?.name || fid }))
  }, [qualifyingMode, flights])

  const teamList = useMemo(() => {
    if (qualifyingMode) {
      return Object.entries(qEvent?.teams || {})
        .map(([tid, t]) => ({ tid, name: [t?.player1, t?.player2].filter(Boolean).join(' / ') || t?.teamName || tid }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    }
    const out = []
    for (const [fid, f] of Object.entries(flights)) {
      if (String(fid).startsWith('_')) continue
      for (const [tid, t] of Object.entries(f?.teams || {})) out.push({ tid, name: t?.info?.name || tid })
    }
    return out.sort((a, b) => String(a.name).localeCompare(String(b.name)))
  }, [qualifyingMode, qEvent, flights])

  // Search box works WITH the dropdown: typing filters the options (name /
  // players). If the current selection is filtered out, the picker still keeps it.
  const filteredTeamList = useMemo(() => {
    const q = teamQuery.trim().toLowerCase()
    if (!q) return teamList
    return teamList.filter((t) => String(t.name).toLowerCase().includes(q) || String(t.tid).toLowerCase().includes(q))
  }, [teamList, teamQuery])

  // When the search narrows to exactly ONE team, select it automatically so the
  // admin can send straight away (no extra click into the dropdown).
  useEffect(() => {
    if (audType === 'team' && teamQuery.trim() && filteredTeamList.length === 1 && teamSel !== filteredTeamList[0].tid) {
      setTeamSel(filteredTeamList[0].tid)
    }
  }, [audType, teamQuery, filteredTeamList, teamSel])

  const waveKeys = useMemo(() => {
    if (qualifyingMode) return []
    const keys = new Set()
    for (const day of Object.values(teeTimes || {})) for (const wk of Object.keys(day?.waves || {})) keys.add(wk)
    return [...keys].sort()
  }, [qualifyingMode, teeTimes])

  // Audience modes available in this mode. Flight/Wave only when they exist.
  const audModes = qualifyingMode
    ? ['everyone', 'team']
    : ['everyone', ...(flightList.length ? ['flight'] : []), ...(waveKeys.length ? ['wave'] : []), 'team']

  const audienceObj = () => {
    if (audType === 'flight') { const f = flightList.find((x) => x.fid === flightId); return { type: 'flight', value: flightId, label: `Flight — ${f?.name || flightId}` } }
    if (audType === 'wave') return { type: 'wave', value: wave, label: wave === 'wave1' ? 'Wave 1 (morning)' : 'Wave 2' }
    if (audType === 'team') { const t = teamList.find((x) => x.tid === teamSel); return { type: 'team', value: teamSel, teamName: t?.name || '', label: `Team — ${t?.name || teamSel}` } }
    return { type: 'everyone', value: null, label: 'Everyone' }
  }

  const centralToUtcMs = (dateStr, timeStr) => {
    const [y, mo, d] = dateStr.split('-').map(Number)
    const [h, mi] = timeStr.split(':').map(Number)
    const utcGuess = Date.UTC(y, mo - 1, d, h, mi)
    const dtf = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    const p = dtf.formatToParts(new Date(utcGuess)).reduce((a, x) => { a[x.type] = x.value; return a }, {})
    let hh = Number(p.hour); if (hh === 24) hh = 0
    const asSeen = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hh, Number(p.minute))
    return utcGuess - (asSeen - utcGuess)
  }

  const aud = audienceObj()
  const scheduledMs = (timing === 'scheduled' && date && time) ? centralToUtcMs(date, time) : null
  const canSend = text.trim() && (audType !== 'flight' || flightId) && (audType !== 'team' || teamSel) && (timing !== 'scheduled' || (date && time))

  const send = async () => {
    if (!canSend || busy) return
    setBusy(true); setNote('')
    try {
      const sendAt = timing === 'scheduled' ? scheduledMs : Date.now()
      const msgId = String(Date.now())
      // pushedAt: null is REQUIRED — the push trigger dedups on it (onPlayerMessage
      // returns early if after.pushedAt is set). Since this is an update() merge, a
      // stale pushedAt from the previous message would otherwise block this send's
      // push entirely (no notification, no log). Clearing it re-arms delivery.
      await update(ref(db, writePath), { msgId, text: text.trim(), audience: aud, sendAt, postedAt: Date.now(), pushedAt: null })
      setNote(timing === 'scheduled' ? '✓ Scheduled — appears on players’ phones at the set time.' : '✓ Sent — showing on targeted players’ phones now.')
      setText('')
    } catch (e) { setNote(e?.message || 'Send failed.') }
    setBusy(false)
  }

  const clear = async () => {
    if (!window.confirm('Clear the current player message? It will disappear from players’ phones.')) return
    setBusy(true); setNote('')
    try { await remove(ref(db, writePath)); setNote('Cleared — no player message is showing.') }
    catch (e) { setNote(e?.message || 'Clear failed.') }
    setBusy(false)
  }

  const centralLabel = scheduledMs ? new Date(scheduledMs).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }) + ' CT' : ''
  const MODE_LABELS = { everyone: 'Everyone', flight: 'By Flight', wave: 'By Wave', team: 'By Team' }
  // Toggle styles — SELECTED is filled gold (stands out on the dark-green admin
  // page; PRIMARY-green would blend into the background); UNSELECTED is a white
  // outline chip. So the current choice is always obvious.
  const selBtn = { background: GOLD, color: PRIMARY, border: `2px solid ${GOLD}`, boxShadow: '0 2px 8px rgba(0,0,0,0.30)' }
  const unselBtn = { background: '#FFFFFF', color: PRIMARY, border: `1.5px solid ${PRIMARY}` }

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-extrabold mb-4" style={{ color: '#FFFFFF' }}>Player Message — Sends to Players (App Only)</h2>

      {current ? (
        <div className="rounded-lg p-3 mb-4 flex items-start justify-between gap-3" style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}>
          <div className="text-sm" style={{ color: '#92400E' }}>
            <b>Currently showing:</b> &ldquo;{current.text}&rdquo;<br />
            <span className="text-sm">To: {current.audience?.label || 'Everyone'}{Number(current.sendAt) > Date.now() ? ` · scheduled ${new Date(Number(current.sendAt)).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' })} CT` : ''}</span>
          </div>
          <button className="btn-ghost text-sm shrink-0" style={{ color: '#B91C1C' }} disabled={busy} onClick={clear}>Clear</button>
        </div>
      ) : null}

      {/* 1) WHO SEES IT — Everyone / By Team (team search appears right below) */}
      <label className="block text-sm font-extrabold uppercase tracking-widest mb-2" style={{ color: '#FFFFFF' }}>1 · Who sees it</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {audModes.map((k) => (
          <button key={k} onClick={() => setAudType(k)} className="text-sm font-extrabold rounded-full px-4 py-2 transition"
            style={audType === k ? selBtn : unselBtn}>{audType === k ? '✓ ' : ''}{MODE_LABELS[k]}</button>
        ))}
      </div>
      {audType === 'flight' ? (
        <select value={flightId} onChange={(e) => setFlightId(e.target.value)} className="border rounded-lg px-2 py-2 text-sm mb-4 w-full max-w-xs" style={{ borderColor: '#D1D5DB' }}>
          <option value="">Choose a flight…</option>
          {flightList.map((f) => <option key={f.fid} value={f.fid}>{f.name}</option>)}
        </select>
      ) : null}
      {audType === 'wave' ? (
        <div className="flex gap-2 mb-4">
          {(waveKeys.length ? waveKeys : ['wave1', 'wave2']).map((wk) => (
            <button key={wk} onClick={() => setWave(wk)} className="text-sm font-extrabold rounded-lg px-3 py-2 transition"
              style={wave === wk ? selBtn : unselBtn}>
              {wave === wk ? '✓ ' : ''}{wk === 'wave1' ? 'Wave 1 (morning)' : wk === 'wave2' ? 'Wave 2' : wk}
            </button>
          ))}
        </div>
      ) : null}
      {audType === 'team' ? (
        <div className="mb-4 w-full max-w-md">
          <input
            type="text"
            value={teamQuery}
            onChange={(e) => setTeamQuery(e.target.value)}
            placeholder="Search teams by name…"
            className="border rounded-lg px-3 py-2 text-sm w-full mb-2"
            style={{ borderColor: '#D1D5DB' }}
          />
          <select value={teamSel} onChange={(e) => setTeamSel(e.target.value)} className="border rounded-lg px-2 py-2 text-sm w-full" style={{ borderColor: '#D1D5DB' }}>
            <option value="">Choose a team…</option>
            {/* Keep the current selection visible even if the search filters it out. */}
            {teamSel && !filteredTeamList.some((t) => t.tid === teamSel)
              ? (() => { const cur = teamList.find((t) => t.tid === teamSel); return cur ? <option value={cur.tid}>{cur.name}</option> : null })()
              : null}
            {filteredTeamList.map((t) => <option key={t.tid} value={t.tid}>{t.name}</option>)}
          </select>
          <div className="text-xs text-gray-300 mt-1">
            {teamQuery.trim() ? `${filteredTeamList.length} of ${teamList.length} teams match` : `${teamList.length} teams`}
          </div>
        </div>
      ) : null}

      {/* 2) WHEN — Instant / Scheduled */}
      <label className="block text-sm font-extrabold uppercase tracking-widest mb-2 mt-2" style={{ color: '#FFFFFF' }}>2 · When</label>
      <div className="flex gap-2 mb-2">
        {[['instant', 'Instant (show now)'], ['scheduled', 'Scheduled']].map(([k, lbl]) => (
          <button key={k} onClick={() => setTiming(k)} className="text-sm font-extrabold rounded-full px-4 py-2 transition"
            style={timing === k ? selBtn : unselBtn}>{timing === k ? '✓ ' : ''}{lbl}</button>
        ))}
      </div>
      {timing === 'scheduled' ? (
        <div className="flex items-center gap-2 mb-1">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" style={{ borderColor: '#D1D5DB' }} />
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" style={{ borderColor: '#D1D5DB' }} />
          <span className="text-sm text-gray-300">Central time</span>
        </div>
      ) : null}
      {timing === 'scheduled' && centralLabel ? <div className="text-sm text-gray-300 mb-4">Will appear: <b>{centralLabel}</b></div> : <div className="mb-4" />}

      {/* 3) MESSAGE */}
      <label className="block text-sm font-extrabold uppercase tracking-widest mb-1 mt-2" style={{ color: '#FFFFFF' }}>3 · Message</label>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={280}
        placeholder="e.g. Dinner is served in the grill at 6:30 — please check in at the tent."
        className="w-full border rounded-lg px-3 py-2 text-sm mb-1" style={{ borderColor: '#D1D5DB' }} />
      <div className="text-sm text-gray-300 mb-4">{text.length}/280</div>

      {/* 4) PREVIEW */}
      <label className="block text-sm font-extrabold uppercase tracking-widest mb-2 mt-2" style={{ color: '#FFFFFF' }}>4 · Preview (what players see)</label>
      <p className="rounded-lg mb-3 text-base font-bold" style={{ background: PRIMARY, color: '#FFFFFF', padding: '10px 14px' }}>👁️ This is a PREVIEW of what players see when you send. (Don’t tap “Got it” — it’s just a mockup.)</p>
      <div className="rounded-2xl p-5 mb-2" style={{ background: PRIMARY }}>
        <div className="text-center text-sm font-extrabold uppercase tracking-widest mb-3" style={{ color: GOLD }}>Message from Tournament</div>
        <div className="text-center font-extrabold" style={{ color: '#fff', fontSize: 26, lineHeight: 1.25 }}>{text.trim() || 'Your message will appear here, big and bold.'}</div>
        <div className="mx-auto mt-5 rounded-full text-center font-extrabold" style={{ background: GOLD, color: PRIMARY, padding: '10px 0', maxWidth: 200 }}>Got it</div>
      </div>
      <div className="text-sm text-gray-200 mb-4">To: <b>{aud.label}</b> · {timing === 'scheduled' ? (centralLabel ? `Scheduled ${centralLabel}` : 'Scheduled (set a date & time)') : 'Instant'}</div>

      <div className="flex items-center gap-3">
        <button onClick={send} disabled={!canSend || busy} className="btn-primary" style={{ backgroundColor: PRIMARY, opacity: (!canSend || busy) ? 0.5 : 1 }}>
          {busy ? 'Sending…' : (timing === 'scheduled' ? '📅 Schedule Message' : '📱 Send to Players Now')}
        </button>
        {note ? <span className="text-sm font-bold" style={{ color: '#FFFFFF' }}>{note}</span> : null}
      </div>
    </div>
  )
}
